import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function LoginPage() {
  const { login, register } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState(null); // null | { valid, trial_days, code }
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (searchParams.get('tab') === 'register') setTab('register');
    if (searchParams.get('ref')) { setReferralCode(searchParams.get('ref')); setTab('register'); }
    if (searchParams.get('promo')) { setPromoCode(searchParams.get('promo').toUpperCase()); setTab('register'); }
  }, [searchParams]);

  // Validate promo on change (debounced)
  useEffect(() => {
    if (!promoCode || promoCode.length < 3) { setPromoStatus(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/promo/validate?code=${encodeURIComponent(promoCode)}`);
        setPromoStatus(data?.valid ? data : { valid: false });
      } catch { setPromoStatus({ valid: false }); }
    }, 350);
    return () => clearTimeout(t);
  }, [promoCode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try { await login(email, password); }
    catch (err) { setError(formatError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try { await register(email, password, name, businessName, referralCode, promoCode); }
    catch (err) { setError(formatError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#F9F8F6', minHeight: '-webkit-fill-available' }}>
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center" style={{ background: '#556B2F' }}>
        {/* Back-to-landing link on the brand panel (desktop) */}
        <Link to="/" className="absolute top-6 left-6 z-20 flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium transition-colors" data-testid="login-back-home-desktop">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
          Back to home
        </Link>
        <div className="relative z-10 text-center px-12">
          <Link to="/" className="inline-block hover:opacity-90 transition-opacity">
            <h1 className="text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Apptio</h1>
          </Link>
          <p className="text-white/80 text-lg leading-relaxed max-w-md mx-auto" style={{ fontFamily: 'Manrope' }}>
            {t('hero.desc')}
          </p>
          <div className="mt-8 flex items-center justify-center gap-6 text-white/60 text-sm">
            <span>{t('hero.check1')}</span>
            <span className="w-1 h-1 rounded-full bg-white/40" />
            <span>{t('hero.check3')}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-start lg:items-center justify-center p-6 sm:p-12 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Top bar: Back-to-landing link (mobile) + language switcher */}
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="lg:hidden flex items-center gap-1.5 text-[#556B2F] hover:text-[#3E5023] text-sm font-medium transition-colors" data-testid="login-back-home-mobile">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
              Back
            </Link>
            <div className="ml-auto"><LanguageSwitcher /></div>
          </div>
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-block hover:opacity-90 transition-opacity">
              <h1 className="text-3xl font-bold text-[#556B2F]" style={{ fontFamily: 'Outfit' }}>Apptio</h1>
            </Link>
            <p className="text-[#5C6656] text-sm mt-1">{t('footer.tagline')}</p>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#E9EDDF]">
              <TabsTrigger data-testid="login-tab" value="login" className="data-[state=active]:bg-[#556B2F] data-[state=active]:text-white">{t('login.loginTab')}</TabsTrigger>
              <TabsTrigger data-testid="register-tab" value="register" className="data-[state=active]:bg-[#556B2F] data-[state=active]:text-white">{t('login.registerTab')}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card className="border-[#E5E7E1] shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('login.welcomeBack')}</CardTitle>
                  <CardDescription className="text-[#5C6656]">{t('login.signInDesc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-[#1A2421]">Email or username</Label>
                      <Input data-testid="login-email-input" type="text" inputMode="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com or username" required className="border-[#E5E7E1]" style={{ fontSize: '16px' }} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1A2421]">{t('login.password')}</Label>
                      <div className="relative">
                        <Input data-testid="login-password-input" type={showPw ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required className="border-[#E5E7E1] pr-10" style={{ fontSize: '16px' }} />
                        <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6656]">
                          {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    {error && <p data-testid="auth-error" className="text-sm text-[#E2725B]">{error}</p>}
                    <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white">
                      {loading ? '...' : <><LogIn size={16} className="mr-2" />{t('login.signIn')}</>}
                    </Button>
                    <div className="text-center">
                      <button type="button" onClick={() => setTab('forgot')} className="text-xs text-[#556B2F] hover:underline" data-testid="forgot-password-link">Forgot password?</button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>


            <TabsContent value="forgot">
              <Card className="border-[#E5E7E1] shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Reset password</CardTitle>
                  <CardDescription className="text-[#5C6656]">Enter your email and we'll send you a reset link.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={async (e) => { e.preventDefault(); setError(''); setLoading(true); try { const API = process.env.REACT_APP_BACKEND_URL; await fetch(`${API}/api/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) }); setError('If an account exists, a reset link has been sent to your email.'); } catch { setError('Failed to send reset email.'); } finally { setLoading(false); } }} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-[#1A2421]">Email</Label>
                      <Input data-testid="forgot-email" type="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="border-[#E5E7E1]" style={{ fontSize: '16px' }} />
                    </div>
                    {error && <p className="text-sm text-[#556B2F]">{error}</p>}
                    <Button type="submit" disabled={loading} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white" data-testid="forgot-submit">{loading ? 'Sending...' : 'Send reset link'}</Button>
                    <div className="text-center"><button type="button" onClick={() => { setTab('login'); setError(''); }} className="text-xs text-[#556B2F] hover:underline">Back to login</button></div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="border-[#E5E7E1] shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('login.createAccount')}</CardTitle>
                  <CardDescription className="text-[#5C6656]">{t('login.registerDesc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-[#1A2421]">{t('login.businessName')}</Label>
                      <Input data-testid="register-business-input" value={businessName} onChange={e => setBusinessName(e.target.value)} className="border-[#E5E7E1]" style={{ fontSize: '16px' }} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1A2421]">{t('login.name')} *</Label>
                      <Input data-testid="register-name-input" value={name} onChange={e => setName(e.target.value)} required className="border-[#E5E7E1]" style={{ fontSize: '16px' }} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1A2421]">{t('login.email')} *</Label>
                      <Input data-testid="register-email-input" type="email" inputMode="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} required className="border-[#E5E7E1]" style={{ fontSize: '16px' }} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#1A2421]">{t('login.password')} *</Label>
                      <Input data-testid="register-password-input" type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="border-[#E5E7E1]" style={{ fontSize: '16px' }} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C6656] text-xs">Referral code (optional)</Label>
                      <Input data-testid="register-referral-input" value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())} placeholder="e.g. APT-A1B2C3D4" className="border-[#E5E7E1] text-sm" />
                      {referralCode && <p className="text-xs text-[#556B2F]">Referred by a friend? Great — they'll earn 1 month free when you subscribe!</p>}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C6656] text-xs">Promo code (optional)</Label>
                      <Input data-testid="register-promo-input" value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} placeholder="e.g. NEW2026" className="border-[#E5E7E1] text-sm" style={{ fontSize: '16px' }} />
                      {promoStatus?.valid && (
                        <p className="text-xs text-[#3E6641] font-medium">✓ {promoStatus.code} applied — {promoStatus.trial_days} days free trial</p>
                      )}
                      {promoStatus && !promoStatus.valid && promoCode.length >= 3 && (
                        <p className="text-xs text-[#E2725B]">Invalid or expired code</p>
                      )}
                    </div>
                    {error && <p data-testid="auth-error" className="text-sm text-[#E2725B]">{error}</p>}
                    <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white">
                      {loading ? '...' : <><UserPlus size={16} className="mr-2" />{t('login.register')}</>}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
